import { type Check, type Smoke, api } from "../lib/api.ts";
import { accountStatus, syncedLabel } from "../lib/format.ts";
import { overviewCopy } from "../lib/overview-copy.ts";
import { type Load, useLoad } from "../lib/useLoad.ts";
/**
 * The screen an operator lands on after SSO.
 *
 * Its job is to answer one question: what can I act on right now, and is the
 * machinery behind it up? It lists the advertisers this caller holds, with the
 * state TikTok reports for each.
 *
 * There is no spend rail. The rail drew a spending cap as a line to a stop, and
 * a TikTok advertiser has no cap on advertiser/info to draw — a rail over nothing
 * would say "no limit" about every account. It comes back with the decision about
 * Business Center budgets; `format.test.ts` keeps it from coming back in passing.
 */
import { Badge, Card, InfoBanner, Page } from "../ui/index.ts";

export function Overview() {
	const [access] = useLoad(() => api.myAccess());
	const [accounts] = useLoad(() => api.tiktokAccounts());
	const [smoke] = useLoad(() => api.smoke());

	const list = accounts.state === "ready" ? accounts.data.accounts : [];
	// One call, and this screen only renders — sentences and banners come back
	// finished from lib/overview-copy.ts, where a runner can reach them.
	const copy = overviewCopy(
		accounts.state,
		list.length,
		accounts.state === "ready" && accounts.data.truncated,
		accounts.state === "failed" ? accounts.status : null,
		accounts.state === "failed" ? accounts.why : null,
	);

	return (
		<Page
			title={copy.headline}
			description={copy.subhead}
			actions={
				access.state === "ready" ? (
					<Badge variant={access.data.inherited ? "info" : "default"}>
						{roleLabel(access.data.role)}
					</Badge>
				) : null
			}
		>
			{copy.banners.map((b) => (
				<InfoBanner key={b.title} variant={b.kind} title={b.title}>
					{b.body}
				</InfoBanner>
			))}

			{list.length > 0 && (
				<ul className="mt-6 grid list-none grid-cols-[repeat(auto-fill,minmax(272px,1fr))] gap-4 p-0">
					{list.map((a) => {
						const status = accountStatus(a.advertiserStatus);
						return (
							<li key={a.id}>
								<Card className="flex h-full flex-col gap-3 p-5">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<h3 className="font-semibold text-content">{a.name}</h3>
											<p className="mt-0.5 font-mono text-xs text-muted">{a.advertiserId}</p>
										</div>
										<Badge variant={badgeVariant(status.tone)}>{status.label}</Badge>
									</div>
									<p className="text-xs text-muted">{syncedLabel(a.syncedAt)}</p>
								</Card>
							</li>
						);
					})}
				</ul>
			)}

			{accounts.state === "failed" && (
				<InfoBanner variant="danger" title="Ad accounts could not be read.">
					{accounts.why}
				</InfoBanner>
			)}

			<section className="mt-10 border-t border-line pt-6">
				<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">Systems</p>
				<div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-px bg-line ring-1 ring-line">
					<System name="TikTok Marketing API" load={smoke} pick={(d) => d.tiktok} />
					<System name="BigQuery" load={smoke} pick={(d) => d.bq} />
					<System name="Database" load={smoke} pick={(d) => d.db} />
				</div>
			</section>
		</Page>
	);
}

const DOT: Record<string, string> = {
	ok: "bg-success",
	error: "bg-danger",
	skipped: "bg-muted",
	unknown: "bg-muted",
};

/**
 * A dependency line names what it actually reached — which database on which
 * instance, which dataset. "Connected" alone does not answer "connected to
 * what?", and on a shared instance that question has a real wrong answer.
 */
function System({
	name,
	load,
	pick,
}: {
	name: string;
	load: Load<Smoke>;
	pick: (d: Smoke) => Check | undefined;
}) {
	const check = load.state === "ready" ? pick(load.data) : undefined;
	const state = load.state === "failed" ? "error" : (check?.state ?? "unknown");
	const target = check?.target;
	const detail =
		load.state === "failed"
			? "Status check unavailable"
			: load.state === "loading"
				? "Checking…"
				: (check?.detail ?? "");

	return (
		<div className="flex items-start gap-3 bg-surface p-4">
			<span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${DOT[state]}`} aria-hidden="true" />
			<div className="min-w-0">
				<p className="text-sm font-semibold text-content">{name}</p>
				{target && <p className="mt-0.5 break-words font-mono text-xs text-content">{target}</p>}
				<p className="mt-0.5 break-words text-xs text-muted">{detail}</p>
			</div>
		</div>
	);
}

export function badgeVariant(tone: string): "success" | "warning" | "error" | "default" {
	if (tone === "ok") return "success";
	if (tone === "warn") return "warning";
	if (tone === "err") return "error";
	return "default";
}

function roleLabel(role: string): string {
	return role === "super_admin"
		? "Super admin"
		: role === "admin"
			? "Admin"
			: role === "manager"
				? "Manager"
				: "User";
}
